import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DeleteOutlined } from '@ant-design/icons';
import { Badge, List, Space, Spin, Typography } from 'antd';

import { useSessionAccessToken } from '@/entities/auth';
import {
    type DeviceSession,
    getCurrentSessionJti,
    transformSessionToDevice,
    useGetSessionsQuery,
    useRevokeSessionMutation,
} from '@/entities/session';

import { formatDeviceLastSeen } from '@/shared/lib/datetime';
import { useAntMessage } from '@/shared/lib/hooks';
import { Button } from '@/shared/ui/button';

import cls from './devices-list.module.css';

export const DevicesList = memo(() => {
    const { t } = useTranslation('profile-management');
    const message = useAntMessage();

    const accessToken = useSessionAccessToken();

    const { data: sessionsData, isLoading, error } = useGetSessionsQuery();
    const [revokeSession, { isLoading: isRevoking }] =
        useRevokeSessionMutation();

    const currentJti = useMemo(
        () => getCurrentSessionJti(accessToken),
        [accessToken],
    );

    const devices = useMemo<DeviceSession[]>(() => {
        if (!sessionsData) return [];

        return sessionsData.map((session) =>
            transformSessionToDevice(session, currentJti),
        );
    }, [sessionsData, currentJti]);

    const handleRemoveDevice = useCallback(
        async (deviceId: string) => {
            try {
                await revokeSession({ jti: deviceId }).unwrap();

                message.success?.({
                    content: t('deviceRemovedSuccessMessage'),
                });
            } catch {
                message.error?.({
                    content:
                        t('deviceRemovalErrorMessage') ||
                        'Failed to remove device',
                });
            }
        },
        [revokeSession, message, t],
    );

    if (isLoading) {
        return (
            <div className={cls.root}>
                <Typography.Title
                    className={cls.title}
                    level={4}
                >
                    {t('devicesTitle')}
                </Typography.Title>

                <Spin size="large" />
            </div>
        );
    }

    if (error) {
        return (
            <div className={cls.root}>
                <Typography.Title
                    className={cls.title}
                    level={4}
                >
                    {t('devicesTitle')}
                </Typography.Title>

                <Typography.Text type="danger">
                    {t('devicesLoadErrorMessage') || 'Failed to load devices'}
                </Typography.Text>
            </div>
        );
    }

    return (
        <div className={cls.root}>
            <Typography.Title
                className={cls.title}
                level={4}
            >
                {t('devicesTitle')}
            </Typography.Title>

            <Typography.Text className={cls.subtitle}>
                {t('devicesSubtitle')}
            </Typography.Text>

            <List
                dataSource={devices}
                renderItem={(device) => (
                    <List.Item className={cls.listItem}>
                        <div className={cls.deviceInfo}>
                            <Space
                                direction="vertical"
                                size={4}
                            >
                                <Space>
                                    <Typography.Text strong>
                                        {device.name}
                                    </Typography.Text>
                                    {device.isCurrent && (
                                        <Badge
                                            className={cls.currentBadge}
                                            color="var(--primary)"
                                            text={t('currentDevice')}
                                        />
                                    )}
                                </Space>

                                <Space size="large">
                                    <Typography.Text>
                                        {device.type}
                                    </Typography.Text>
                                    <Typography.Text>
                                        {t('lastUsed')}:{' '}
                                        {formatDeviceLastSeen(device.lastUsed)}
                                    </Typography.Text>
                                </Space>
                            </Space>
                        </div>

                        {!device.isCurrent && (
                            <Button
                                className={cls.removeButton}
                                icon={<DeleteOutlined />}
                                loading={isRevoking}
                                onClick={() => handleRemoveDevice(device.id)}
                                size="small"
                                title={t('removeDevice')}
                                variant="secondary"
                            />
                        )}
                    </List.Item>
                )}
            />
        </div>
    );
});

DevicesList.displayName = 'DevicesList';
